import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Star, CheckCircle2, AlertTriangle, Home, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TestimonialSubmit() {
  const { token } = useParams();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/api/testimonials/invite/${token}`);
        if (res.data?.customer_name) setName(res.data.customer_name);
        setValid(true);
      } catch (err) {
        setError(err.response?.data?.detail || "Ugyldig invitasjonslenke");
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      toast.error("Vennligst oppgi navnet ditt");
      return;
    }
    if (comment.trim().length < 10) {
      toast.error("Skriv minst 10 tegn i kommentaren");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/testimonials/invite/${token}/submit`, {
        name: name.trim(),
        rating,
        comment: comment.trim(),
      });
      setSubmitted(true);
      toast.success("Takk for din anmeldelse!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke sende anmeldelsen");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <Card className="bg-[#0A0A0A] border-red-500/30 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Ugyldig lenke</h1>
            <p className="text-[#94A3B8] mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-[#F7931A] text-white">
                <Home className="w-4 h-4 mr-2" />
                Tilbake til forsiden
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <Card className="bg-[#0A0A0A] border-[#23F7DD]/40 max-w-md w-full">
          <CardContent className="p-8 text-center" data-testid="testimonial-thank-you">
            <CheckCircle2 className="w-20 h-20 text-[#23F7DD] mx-auto mb-4" />
            <h1 className="text-3xl font-['Unbounded'] font-bold text-white mb-3">
              Tusen takk!
            </h1>
            <p className="text-[#94A3B8] mb-6">
              Anmeldelsen din er mottatt og vil bli publisert etter en kort gjennomgang.
            </p>
            <Link to="/">
              <Button className="bg-[#F7931A] text-white">
                <Home className="w-4 h-4 mr-2" />
                Tilbake til forsiden
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-block px-4 py-2 bg-[#F7931A]/20 border border-[#F7931A]/40 rounded-full mb-4">
            <span className="text-[#F7931A] font-semibold text-sm">AllStillas Anmeldelse</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-['Unbounded'] font-bold text-white mb-3">
            Del din opplevelse
          </h1>
          <p className="text-[#94A3B8]">
            Din tilbakemelding hjelper andre kunder med å velge AllStillas
          </p>
        </div>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="testimonial-form">
              {/* Rating Stars */}
              <div>
                <label className="block text-white font-medium mb-3">
                  Din vurdering <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHoverRating(n)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="transition-transform hover:scale-110"
                      data-testid={`rating-star-${n}`}
                    >
                      <Star
                        className={`w-10 h-10 transition-colors ${
                          n <= (hoverRating || rating)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-[#475569]"
                        }`}
                      />
                    </button>
                  ))}
                  <span className="ml-3 text-[#94A3B8] text-sm">
                    {rating} / 5
                  </span>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Ditt navn <span className="text-red-400">*</span>
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ola Nordmann"
                  className="bg-black/40 border-white/20 text-white"
                  maxLength={100}
                  required
                  data-testid="testimonial-name-input"
                />
              </div>

              {/* Comment */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Din kommentar <span className="text-red-400">*</span>
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Fortell oss om din opplevelse med AllStillas..."
                  className="bg-black/40 border-white/20 text-white min-h-[140px]"
                  maxLength={2000}
                  required
                  data-testid="testimonial-comment-input"
                />
                <div className="text-right text-xs text-[#64748B] mt-1">
                  {comment.length} / 2000 tegn
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#F7931A] text-white hover:bg-[#F7931A]/80 py-6 text-base font-semibold"
                data-testid="submit-testimonial-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Sender...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send anmeldelse
                  </>
                )}
              </Button>

              <p className="text-xs text-[#64748B] text-center">
                Anmeldelsen din blir publisert etter en kort gjennomgang av administrator.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
